import { useRef, useEffect, useState } from "react"
import { useDrop } from "react-dnd"
import { useSelector, useDispatch } from "react-redux"

import dragTypes from "utils/scripts/dragTypes"
import { arraysAreEqual } from "utils/scripts/useMemoizeArray"

import { Popup } from "components"
import { WhiteTaskConfig } from "components/Config"
import { DateTime } from "luxon"

import {
  scheduleHover,
  message,
  whiteTasks,
  analysisTasks,
  projectActions,
  analyses,
  projects,
  multipleDrag,
  iap,
  waiting,
  teamStatus,
} from "state_management"
import useT from "utils/hooks/useT"
import { isTaskBeyondMoveGrace } from "utils/scripts/lockGrace"
import { Button } from "react-bootstrap"

// SPEC-047 (R17): a task counts as "in the past" when its CURRENT start lies before today 00:00 in
// the team timezone (the backend lock boundary). A task with no start is treated as future
// (movable). On an executed analysis (DONE/FAILED samples) only PAST tasks are frozen — future
// tasks may be moved freely, including dragged back into the past.
// SPEC-066 V4 (#4166): this strict boundary now applies to the MULTI-select bulk move only; the
// single-task drop uses isTaskBeyondMoveGrace (three-day manual grace) instead — see below.
function isTaskStartInPast(start, teamTimeZone) {
  if (!start) return false
  return DateTime.fromISO(start) < DateTime.now().setZone(teamTimeZone).startOf("day")
}

// A single hour in the schedule.
// If this is clicked, a config for adding a white task is opened.
export default function ScheduleElementHour({ memberId, date, interval, task, isMobile }) {
  const { t } = useT("messages")
  const { t: tc } = useT("common")
  const member = useSelector((state) => state.members[memberId])
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone)
  const isViewOnly = useSelector((state) => state.auth.isViewOnly)
  const isMember = useSelector((state) => state.auth.isMember)
  const allowTaskTransfer = useSelector((state) => state.teamStatus.allowTaskTransfer)
  const authMemberId = useSelector((state) => state.auth.memberId)
  const currentMemberShowOnPlan = useSelector((state) => authMemberId && state.members[authMemberId] ? state.members[authMemberId].showOnPlan : true)
  const isReadOnly = isViewOnly || (isMember && !allowTaskTransfer && (!currentMemberShowOnPlan || memberId !== authMemberId))
  const dispatch = useDispatch()
  const [activeConfig, setActiveConfig] = useState(false)
  const [greenTaskConfig, setGreenTaskConfig] = useState(false)
  const [mobileChoice, setMobileChoice] = useState(false)
  const [bookedAndFreeAssets, setBookedAndFreeAssets] = useState({ bookedAssets: [], freeAssets: [] })
  const IAPconfig = useSelector((state) => state.visual.IAPconfig)
  const IAPconfigRef = useRef(IAPconfig)
  // SPEC-047 (R17): an analysis carrying DONE/FAILED samples (hasCompletedOrFailedSamples) freezes
  // only its PAST tasks — the backend refuses moving a task whose current start is before today
  // 00:00 team tz (406), but a FUTURE task may be moved freely, including dragged back into the
  // past. The schedule tile stays draggable (dragging is not gated), so refuse PER-TASK at the
  // drop, exactly like the SPEC-045 K3 unplan-drop guard in AnalysisTasksMenu. plannedAnalyses
  // carries the authoritative hasCompletedOrFailedSamples flag; multipleDragItems is the current
  // multi-select, each item judged (analysis executed AND its own start in the past) before a
  // bulk move.
  const plannedAnalyses = useSelector((state) => state.analyses.plannedAnalyses)
  const multipleDragItems = useSelector((state) => state.multipleDrag.items)

  useEffect(() => {
    IAPconfigRef.current = IAPconfig
  }, [IAPconfig])

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: Object.values(dragTypes),
      collect: (mon) => ({
        isOver: !!mon.isOver(),
      }),
      drop: ({ type, item }) => {
        if (isReadOnly) return
        const start = date


        const recurrenceRes = {
          name: item.name,
          start: date,
          oldStart: date,
          duration: item.duration,
          description: item.description,
          memberIds: item.memberIds,
          assetIds: item.assetIds,
          greenTask: false,
          amountOfReoccurrences: item.recurrenceAmountOfReoccurrences,
          frequency: item.recurrenceFrequency,
          interval: item.recurrenceInterval,
        }

        if (type === dragTypes.MULTIPLETASK) {
          // SPEC-073 (R3): no task of an analysis sent to QC review moves, whatever its date — the
          // backend 406s the whole batch.
          if (Object.values(multipleDragItems).some((dragItem) => plannedAnalyses[dragItem.analysisId]?.inQcReview)) {
            dispatch(message.warning(tc("analysisInQcReview")))
            return
          }
          // SPEC-047 (R17): refuse the bulk move only when a selected task that is CURRENTLY in the
          // past sits on an executed analysis (DONE/FAILED samples) — that task is frozen and the
          // backend would 406 the batch. Future tasks (even on an executed analysis) may bulk-shift.
          if (
            Object.values(multipleDragItems).some(
              (dragItem) =>
                plannedAnalyses[dragItem.analysisId]?.hasCompletedOrFailedSamples &&
                isTaskStartInPast(dragItem.start, teamTimeZone)
            )
          ) {
            dispatch(message.warning(tc("blockedActionPastTaskCompleted")))
            return
          }
          dispatch(multipleDrag.dropAllTasks([memberId], start))
        } else if (type === dragTypes.MULTIPLEACTION) {
          dispatch(multipleDrag.dropAllActions([memberId], start))
        } else if (type === dragTypes.STANDARD_ACTION) {
          dispatch(projectActions.planStandardAction(item.id, { start, memberIds: [member.id] }))
        } else if (type === dragTypes.ACTION) {
          dispatch(projectActions.move(item.id, { start, memberIds: [member.id] }))
        } else if (type === dragTypes.SMART_DRAG) {
          const input = { memberId: member.id }

          if (item.__typename === "AnalysisTask") {
            input.analysisId = item.analysisId
          } else {
            input.unplannedAnalysisId = item.id
          }

          dispatch(
            teamStatus.setIAPconfiguration(IAPconfigRef.current.timePeriod.from, IAPconfigRef.current.timePeriod.to)
          ).then(() => {
            dispatch(waiting.changeIapLoading(tc("runningIAP")))
            dispatch(iap.runFillInSingleAnalysis(input)).finally(() => dispatch(waiting.changeIapLoading("")))
          })
        } else if (type === dragTypes.ANALYSIS_TASK || type === dragTypes.WHITE_TASK) {
          let memberIds = item.memberIds.slice()
          if (!memberIds.includes(member.id)) {
            if (memberIds.length > 1) {
              memberIds.push(member.id)
            } else {
              memberIds = [member.id]
            }
          }
          if (item.__typename === "WhiteTask") {
            if (item["recurrenceId"] && !arraysAreEqual(item.memberIds, memberIds)) {
              dispatch(message.warning(t("adhoc.cannotDragRecurrentTask")))
            } else if (item["recurrenceId"] && !item.fromOutlook) {
              recurrenceRes.whiteTaskId = item.recurrenceId
              dispatch(whiteTasks.editRecurrence(item.recurrenceId, recurrenceRes, item.greenTask))
            } else if (!item.fromOutlook) {
              dispatch(whiteTasks.move(item.id, { start, memberIds }))
            } else if (item.fromOutlook) {
              dispatch(message.warning(t("adhoc.cannotMoveImportedTasks")))
            }
          } else {
            // SPEC-047 (R17) + SPEC-068 R21 (#4166): an executed analysis (DONE/FAILED samples)
            // freezes only tasks that were due MORE THAN 3 calendar days ago — refuse the drop only
            // then (the backend would 406 it). A task due within the last 3 days, or a FUTURE task,
            // falls through to the move below, freely (forwards or back into the past). The
            // multi-select branch above keeps the strict today-00:00 rule, as the server does.
            // SPEC-073 (R3): an analysis sent to QC review is frozen — no grace applies.
            if (plannedAnalyses[item.analysisId]?.inQcReview) {
              dispatch(message.warning(tc("analysisInQcReview")))
              return
            }
            if (
              plannedAnalyses[item.analysisId]?.hasCompletedOrFailedSamples &&
              isTaskBeyondMoveGrace(item.start, teamTimeZone)
            ) {
              dispatch(message.warning(tc("blockedActionTaskBeyondMoveGrace")))
              return
            }
            // SPEC-046 follow-up: a drag-move only replaces the task in the store — it never
            // refreshes the analysis. The backend auto-locks the analysis when the task lands in the
            // past (placement lock, one-day grace, or full hard-lock), so without this the lock/ghost
            // state (movable / samplesLocked / earliestTaskStart) stays stale until a manual refresh.
            // getAnalysis re-reads the analysis into plannedAnalyses, mirroring the dialog-edit path
            // (setMovable) and the bulk-move path (fetchPlanned).
            dispatch(analysisTasks.move(item.id, { start, memberIds })).then(() =>
              dispatch(analyses.getAnalysis(item.analysisId))
            )
          }
        } else if (type === dragTypes.ANALYSIS) {
          dispatch(analyses.moveIntoPlan(item.id, start, member))
        } else {
          dispatch(projects.moveIntoPlan(item.id, start, member))
        }
      },
    }),
    [dragTypes, date, plannedAnalyses, multipleDragItems]
  )

  // Cache DateTime computations for date
  const dateInZone = DateTime.fromISO(date, { zone: teamTimeZone })
  const dateInThisTimezone = DateTime.fromISO(date, { zone: teamTimeZone })
  const dateInZoneMillis = dateInZone.toMillis()
  const dateInThisTimezoneMillis = dateInThisTimezone.toMillis()
  const dateInZoneStartOfDayMillis = dateInZone.startOf('day').toMillis()
  const memberWorkHoursMinMillis = member.workHours.min * 1000
  const memberWorkHoursMaxMillis = member.workHours.max * 1000

  const availableOnDate = {
    1: member.workWeek.monday,
    2: member.workWeek.tuesday,
    3: member.workWeek.wednesday,
    4: member.workWeek.thursday,
    5: member.workWeek.friday,
    6: member.workWeek.saturday,
    7: member.workWeek.sunday,
  }

  const currentWeekday = dateInZone.weekday;

  // Inline calculation for previous weekday
  const previousWeekday = currentWeekday === 1 ? 7 : currentWeekday - 1;

  const isPreviousDayWorkday = availableOnDate[previousWeekday];

  const overMidnight = member.workHours.min > member.workHours.max;

  const timeSinceStartOfDayMillis = dateInZoneMillis - dateInZoneStartOfDayMillis;

  if (!overMidnight) {
    // Handle normal shifts
    if (
      !availableOnDate[currentWeekday] ||
      timeSinceStartOfDayMillis < memberWorkHoursMinMillis ||
      timeSinceStartOfDayMillis >= memberWorkHoursMaxMillis
    ) {
      return <div className="ScheduleElement-div invalid-area" />;
    }
  } else {
    // Handle overnight shifts
    if (
      !(
        // Current day part of the overnight shift
        (availableOnDate[currentWeekday] &&
          timeSinceStartOfDayMillis >= memberWorkHoursMinMillis) ||
        // Early morning part coming from the previous day's overnight shift
        (isPreviousDayWorkday &&
          timeSinceStartOfDayMillis < memberWorkHoursMaxMillis)
      )
    ) {
      return <div className="ScheduleElement-div invalid-area" />;
    }
  }

  const dateInZoneFormatted = dateInThisTimezone.toFormat("HH:mm")
  const dateInZonePlusInterval = DateTime.fromMillis(dateInThisTimezoneMillis + interval * 3600000).setZone(teamTimeZone)
  const dateInZonePlusIntervalFormatted = dateInZonePlusInterval.toFormat("HH:mm")
  const dateInZoneISO = dateInZone.toISO()

  return (
    <>
      {mobileChoice && (
        <Popup nonScrollable onCancel={() => setMobileChoice(false)}>
          <div className="ScheduleElementHour-mobileChoice">
            <h4>{tc("chooseWhatToAdd")}</h4>
            <div className="ScheduleElementHour-mobileChoice-content">
              <Button
                variant="primary"
                onClick={() => {
                  setMobileChoice(false)
                  setActiveConfig(true)
                }}
              >
                {tc("addGreyTask")}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setMobileChoice(false)
                  setGreenTaskConfig(true)
                }}
              >
                {task ? tc("editOffsite") : tc("addOffsite")}
              </Button>
            </div>
            <div className="ScheduleElementHour-mobileChoice-cancel">
              <Button variant="secondary" onClick={() => setMobileChoice(false)}>
                {tc("close")}
              </Button>
            </div>
          </div>
        </Popup>
      )}
      {activeConfig && (
        <Popup nonScrollable onCancel={() => setActiveConfig(false)}>
          <WhiteTaskConfig
            task={{
              start: dateInZoneISO,
              duration: interval * 3600, // Duration in seconds
              memberIds: [memberId],
            }}
            onClose={() => {
              setActiveConfig(false)
            }}
            type="team-plan"
            bookedAndFreeAssets={bookedAndFreeAssets}
          />
        </Popup>
      )}
      {greenTaskConfig && (
        <Popup
          className={isMobile ? "Mobile-Green-task-config-popup" : "Green-task-config-popup"}
          onCancel={() => setGreenTaskConfig(false)}
        >
          <WhiteTaskConfig
            task={
              task || {
                start: dateInZoneISO,
                duration: 3600, // 1 hour in seconds
                memberIds: [memberId],
                greenTask: true,
                name: date + memberId,
              }
            }
            onClose={() => setGreenTaskConfig(false)}
            type="team-plan"
          />
        </Popup>
      )}
      <div
        ref={drop}
        className={"ScheduleElement-div" + (task ? " green-task" : "") + (isOver ? " drag-over" : "")}
        onMouseLeave={() => dispatch(scheduleHover.setMemberHour(null, null))}
        onMouseEnter={() => dispatch(scheduleHover.setMemberHour(dateInZoneFormatted, memberId))}
        onDoubleClick={() => {
          if (isReadOnly) return
          isMobile ? setMobileChoice(true) : setActiveConfig(true)
          dispatch(whiteTasks.getBookedFreeAssets(null, { start: dateInZoneISO })).then((res) => {
            setBookedAndFreeAssets(res)
          })
        }}
        onClick={(e) => {
          if (isReadOnly) return
          if (e.shiftKey) {
            setGreenTaskConfig(true)
            // Green task config contains input elements and user-select: none; can't be used properly
            document.getSelection().removeAllRanges()
          }
        }}
        title={`${dateInZoneFormatted} ${tc("dateTo")} ${dateInZonePlusIntervalFormatted}`}
      />
    </>
  )
}



